import useOptimisticMoveProjectToPipelineStage from '@/api/mutations/project/optimistic/useOptimisticMoveProjectToPipelineStage';
import useGetPipeline from '@/api/queries/pipelines/useGetPipeline';
import useGetProjectPotentialPipelineTransitions from '@/api/queries/project/useGetProjectPotentialPipelineTransitions';
import {
  getDisallowedTransitionsSet,
  getTransitionGuardResult,
} from '@/helpers/pipelinesHelper';
import { useTranslation } from '@/i18n';
import {
  Combobox,
  SelectOption,
  SelectSizeVariant,
  useToastContext,
} from '@payaca/ambrosia';
import { PipelineGuardResultGroups } from '@payaca/components/pipelineStage/GuardResultDisplay';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';

import { FC, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';

type StageOptionMetadata = {
  blocked?: boolean;
  colour?: string | null;
};

const PipelineStageControl: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useGetProjectByIdOverview(projectId);

  if (!projectId) return null;

  return (
    <V2PipelineStageControl
      projectId={projectId}
      pipelineId={project?.pipeline?.publicId}
    />
  );
};

const V2PipelineStageControl: FC<{
  projectId: string;
  pipelineId?: string;
}> = ({ projectId, pipelineId }) => {
  const t = useTranslation();
  const { pushToast } = useToastContext();
  const {
    potentialPipelineTransitions,
    isLoading: isLoadingPotentialPipelineTransitions,
  } = useGetProjectPotentialPipelineTransitions(projectId);
  const { data: pipelineData } = useGetPipeline(pipelineId);
  const { project } = useGetProjectByIdOverview(projectId);
  const { mutateAsync } = useOptimisticMoveProjectToPipelineStage(pipelineId);

  const disallowedTransitionsSet = potentialPipelineTransitions
    ? getDisallowedTransitionsSet(potentialPipelineTransitions)
    : new Set<string>();

  if (!project) return null;

  const options: SelectOption<string, StageOptionMetadata>[] =
    pipelineData?.pipeline?.stages.map((stage) => ({
      value: stage.publicId,
      label: stage.title,
      disabled: isLoadingPotentialPipelineTransitions,
      metadata: {
        blocked: disallowedTransitionsSet.has(stage.publicId),
        colour: stage.colour,
      },
    })) ?? [
      {
        value: project.currentPipelineStage.publicId,
        label: project.currentPipelineStage.title,
      },
    ];

  // Blocked stages surface the guard reason in a toast instead of moving. The
  // dropdown closes on any selection (Headless UI single-select behaviour).
  const handleChange = (value: string | null) => {
    if (!value) return;
    if (!project.currentPipelineStage.publicId) return;

    if (disallowedTransitionsSet.has(value)) {
      const guardResultGroups = potentialPipelineTransitions
        ? getTransitionGuardResult(t, potentialPipelineTransitions, value)
        : undefined;
      const stageName =
        pipelineData?.pipeline?.stages.find((stage) => stage.publicId === value)
          ?.title ?? 'this stage';

      if (guardResultGroups) {
        pushToast({
          variant: 'white',
          dismissible: 'button',
          message: (
            <GuardReasonToast
              stageName={stageName}
              groups={guardResultGroups}
            />
          ),
        });
      }
      return;
    }

    void mutateAsync({
      projectPublicId: projectId,
      targetPipelineStagePublicId: value,
      currentPipelineStagePublicId: project.currentPipelineStage.publicId,
    });
  };

  return (
    <Combobox<string, false, StageOptionMetadata>
      CustomSelected={StageSelected}
      CustomOption={StageOption}
      sizeVariant={SelectSizeVariant.SM}
      value={project.currentPipelineStage.publicId}
      options={options}
      onChange={(value) => {
        if (Array.isArray(value)) return;
        handleChange(value);
      }}
    />
  );
};

const StageColourDot: FC<{ colour?: string | null }> = ({ colour }) => (
  <div
    className="h-3 w-3 shrink-0 rounded-full"
    style={{ backgroundColor: colour ?? undefined }}
  />
);

const StageSelected: FC<{
  selectedOptions: SelectOption<string, StageOptionMetadata>[];
}> = ({ selectedOptions }) => {
  const selected = selectedOptions[0];
  if (!selected) return null;

  return (
    <div className="flex items-center gap-1.5">
      <b>Stage:</b>
      <StageColourDot colour={selected.metadata?.colour} />
      {selected.label}
    </div>
  );
};

/**
 * Renders a stage's colour dot and title. Blocked stages show red text to
 * signal they can't be moved to; clicking one surfaces the guard reason
 * (handled in the control's onChange). A visually-hidden suffix conveys the
 * blocked state to screen readers, which colour alone cannot.
 */
const StageOption: FC<{
  option: SelectOption<string, StageOptionMetadata>;
}> = ({ option }) => (
  <div className="flex flex-1 items-center gap-1.5">
    <StageColourDot colour={option.metadata?.colour} />
    <span className={clstx(option.metadata?.blocked && 'text-red-600')}>
      {option.label}
      {option.metadata?.blocked && <span className="sr-only"> (blocked)</span>}
    </span>
  </div>
);

/**
 * Toast body explaining why a project can't move to a stage. Keeps the guard's
 * OR-of-ANDs hierarchy but with lighter styling than the kanban display.
 */
const GuardReasonToast: FC<{
  stageName: string;
  groups: PipelineGuardResultGroups;
}> = ({ stageName, groups }) => {
  const hasMultipleGroups = groups.or.length > 1;

  return (
    <div className="supporting-body flex max-w-xs flex-col gap-2">
      <p className="font-medium">Cannot move Project to {stageName}</p>
      {groups.or.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && <div className="text-gray-500">or</div>}
          <div
            className={clstx(
              'flex flex-col gap-1',
              hasMultipleGroups && 'rounded-md border border-gray-200 p-2'
            )}
          >
            {group.and.map((condition, conditionIndex) => (
              <Fragment key={conditionIndex}>
                {conditionIndex > 0 && <div className="text-gray-500">and</div>}
                <div className="flex items-start gap-2">
                  {/* h-5 matches the text-sm line height so the icon centres
                      on the first line even when the description wraps. */}
                  <span className="flex h-5 shrink-0 items-center">
                    <UntitledIcon
                      name={
                        condition.isSatisfied ? 'check-circle.3' : 'circle.4'
                      }
                      className={clstx(
                        'h-4 w-4',
                        condition.isSatisfied
                          ? 'text-green-600'
                          : 'text-gray-400'
                      )}
                    />
                  </span>
                  <span>{condition.description}</span>
                  <span className="sr-only">
                    {condition.isSatisfied ? '(met)' : '(not met)'}
                  </span>
                </div>
              </Fragment>
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export default PipelineStageControl;
